import { GRIDSTATE } from '.'
import { Timeout } from '../../types'
import { GridState } from '../../types/state'
import { getFloaterState } from '../components/Hooks/useModal'
import { setLargePreview } from '../components/presentation/Grid/helpers'
import { ExploreCategory } from '../constants/enums'
import { FLOATING_PREVIEW } from '../constants/floaters'
import { customStateWrapper } from './customStateWrapper'
import { generateModalKey } from './internal/modals'
import { pubSubGrid, pubSubUsers } from './pubSub'

export {
    isScrollCursorTile,
    resetScrollCursorState,
    scrollCursorChannel,
    setScrollCursorTargetState,
} from './internal/gridScrollCursor'

type SetGridStateOptions = { immediate?: boolean }

const setGridState = customStateWrapper<GridState, SetGridStateOptions>(
    (_updates, { immediate = false } = {}) => {
        pubSubGrid.emit('state', GRIDSTATE.grid, immediate)
    },
)((updates) => {
    GRIDSTATE.grid = { ...GRIDSTATE.grid, ...updates }
})

const avatarInfoOpenTimers: Record<number, Timeout> = {}

const exploreScrollPositions = {
    [ExploreCategory.Popular]: 0,
    [ExploreCategory.Trending]: 0,
    [ExploreCategory.MissMFC]: 0,
    [ExploreCategory.NewModels]: 0,
    [ExploreCategory.GroupShows]: 0,
}

pubSubUsers.on(
    'UPDATE:isPublicShow',
    ({ current: isPublicShow, id }: { current: boolean; id: number }) => {
        const largePreview = getFloaterState(
            generateModalKey(FLOATING_PREVIEW, id),
        )()
        if (
            !largePreview || // not open
            isPublicShow
        ) {
            return
        }

        setLargePreview(id, null, false, false)
    },
)

// pubSubGrid.on('state', (data) => {
//     pubSubGrid.emit(
//         `state::${data?.state?.centeredIndex}`,
//         data?.state?.centeredIndex,
//     )
// })

export const getGridStateSelector = (attribute: keyof typeof GRIDSTATE.grid) =>
    GRIDSTATE.grid[attribute]

type PreviewOnScrollIndex = {
    column: number | null
    row: number | null
}

const previewOnScrollIndexDefault: PreviewOnScrollIndex = {
    column: null,
    row: null,
}

export const temporaryGridState = {
    centeredIndex: 0,
    lists: {},
    previewOnScrollIndex: previewOnScrollIndexDefault,
}

export const setCenteredIndexState = (centeredIndex) => {
    const currentCenteredIndex = temporaryGridState.centeredIndex //getGridState()?.centeredIndex

    if (centeredIndex === currentCenteredIndex) {
        return
    }

    if (currentCenteredIndex !== undefined) {
        pubSubGrid.emit(`state::${currentCenteredIndex}`, null)
    }

    temporaryGridState.centeredIndex = centeredIndex

    pubSubGrid.emit(`state::${centeredIndex}`, centeredIndex)

    // setGridState({ modifications: { centeredIndex } })
}

export const resetCenteredIndexState = () => {
    temporaryGridState.centeredIndex = 0
}

export const previewOnScrollChannel = (row: number, column: number) =>
    `preview::${row}:${column}`

export const isPreviewOnScrollTile = (row: number, column: number) => {
    const { previewOnScrollIndex } = temporaryGridState

    return (
        previewOnScrollIndex.row === row &&
        previewOnScrollIndex.column === column
    )
}

export const setPreviewOnScrollIndexState = (
    row: number | null,
    column: number | null,
) => {
    const current = temporaryGridState.previewOnScrollIndex

    if (current.row === row && current.column === column) {
        return
    }

    temporaryGridState.previewOnScrollIndex = { column, row }

    // notify only the affected tiles, like the centered-index channels above
    if (typeof current.row === 'number') {
        pubSubGrid.emit(
            previewOnScrollChannel(current.row, current.column),
            false,
        )
    }

    if (typeof row === 'number') {
        pubSubGrid.emit(previewOnScrollChannel(row, column), true)
    }
}

export const resetPreviewOnScrollIndexState = () => {
    setPreviewOnScrollIndexState(null, null)
}

//
// Mouse momentum for desktop hover previews. Module state read at event
// time — a flip must not re-render the grid; only a tile that armed itself
// (moused-in during fast movement) is notified when movement settles.
//
const HOVER_MOMENTUM_SETTLED = 'hoverMomentum::settled'
const MOUSE_MOMENTUM_THRESHOLD = 1
const MOUSE_MOMENTUM_SETTLE_MS = 100

const mouseMomentum = {
    high: false,
    disabled: false,
    lastTime: 0,
    lastX: 0,
    lastY: 0,
    settleTimeout: null as Timeout | null,
    trackers: 0,
}

export const isMouseMovementHighState = () => mouseMomentum.high

export const onHoverMomentumSettled = (callback: () => void) =>
    pubSubGrid.on(HOVER_MOMENTUM_SETTLED, callback)

const setMomentumHigh = (high: boolean) => {
    if (mouseMomentum.high === high) {
        return
    }

    mouseMomentum.high = high

    if (!high) {
        pubSubGrid.emit(HOVER_MOMENTUM_SETTLED, true)
    }
}

const scheduleMomentumSettle = () => {
    clearTimeout(mouseMomentum.settleTimeout)

    //
    // Marc: Sometimes when the user stops the mouse movement abruptly, the
    // velocity can be a really high value... We want to make sure to
    // register that it stopped.
    //
    mouseMomentum.settleTimeout = setTimeout(() => {
        mouseMomentum.disabled = false
        setMomentumHigh(false)
    }, MOUSE_MOMENTUM_SETTLE_MS)
}

const handleMouseMomentumMove = (event: MouseEvent) => {
    const currentTime = performance.now()
    const timeDelta = currentTime - mouseMomentum.lastTime

    if (timeDelta < 10) {
        return
    }

    const distanceDeltaY = event.clientY - mouseMomentum.lastY
    const distanceDeltaX = event.clientX - mouseMomentum.lastX

    mouseMomentum.lastY = event.clientY
    mouseMomentum.lastX = event.clientX
    mouseMomentum.lastTime = currentTime

    const velocity =
        Math.abs(distanceDeltaY / timeDelta) +
        Math.abs(distanceDeltaX / timeDelta)

    if (velocity > MOUSE_MOMENTUM_THRESHOLD) {
        scheduleMomentumSettle()

        if (mouseMomentum.disabled) {
            return
        }

        mouseMomentum.disabled = true
        setMomentumHigh(true)
        return
    }

    if (velocity > 0.1 && mouseMomentum.disabled) {
        scheduleMomentumSettle()
        return
    }

    mouseMomentum.disabled = false
    setMomentumHigh(false)
}

// Reference-counted: the home grid stays mounted under a fullscreen room
// whose available-space grid also runs useHomeData, so one consumer
// unmounting must not stop tracking for the other.
export const startMouseMomentumTracking = () => {
    if (typeof window === 'undefined') {
        return
    }

    mouseMomentum.trackers += 1

    if (mouseMomentum.trackers > 1) {
        return
    }

    mouseMomentum.lastTime = performance.now()
    window.addEventListener('mousemove', handleMouseMomentumMove)
}

export const stopMouseMomentumTracking = () => {
    if (mouseMomentum.trackers < 1) {
        return
    }

    mouseMomentum.trackers -= 1

    if (mouseMomentum.trackers > 0) {
        return
    }

    window.removeEventListener('mousemove', handleMouseMomentumMove)
    clearTimeout(mouseMomentum.settleTimeout)
    mouseMomentum.disabled = false
    mouseMomentum.high = false
}

export const clearAvatarInfoOpenTimer = (id: number) => {
    clearTimeout(avatarInfoOpenTimers[id])
    delete avatarInfoOpenTimers[id]
}

const clearAllAvatarInfoOpenTimers = () => {
    Object.keys(avatarInfoOpenTimers).forEach((id) =>
        clearAvatarInfoOpenTimer(Number(id)),
    )
}

const setAvatarInfoOpenIDState = (avatarInfoOpenID: number | null) => {
    setGridState({ avatarInfoOpenID })
}

export const resetAvatarInfoOpenIDState = () => {
    clearAllAvatarInfoOpenTimers()
    setAvatarInfoOpenIDState(null)
}

export const setAvatarInfoOpenID = (id: number | null) => {
    clearAllAvatarInfoOpenTimers()
    setAvatarInfoOpenIDState(id)
}

export const clearAvatarInfoOpenID = (id: number) => {
    // this is a hack to prevent the avatar info from closing when the list
    // refreshes (due to the tile unmounting and remounting in the current
    // implementation)
    avatarInfoOpenTimers[id] = setTimeout(() => {
        if (getGridStateSelector('avatarInfoOpenID') === id) {
            resetAvatarInfoOpenIDState()
        } else {
            clearAvatarInfoOpenTimer(id)
        }
    }, 1000)
}

export const setExploreScrollPosition = (
    category: ExploreCategory,
    scrollPosition: number,
) => {
    exploreScrollPositions[category] = scrollPosition
}

export const getExploreScrollPosition = (category: ExploreCategory) =>
    exploreScrollPositions[category]

export const resetExploreScrollPositions = () => {
    Object.keys(exploreScrollPositions).forEach((key) => {
        exploreScrollPositions[key] = 0
    })
}
